import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Button, Dialog, DialogTitle, Typography } from "@material-ui/core";

import { trackEventDeletionRequest } from "utils/gAnalyticsUtils";
import UserPresenter from "presenters/UserPresenter";
import { appRoutes, externalRoutes } from "routes";
import { useUsers, useNotifications } from "hooks";
import SpinningCloseButton from "components/SpinningCloseButton";

import LinkStyledButton from "../LinkStyledButton";

import useStyles from "./useStyles";

const DeleteAccount = () => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  const { currentUser, updateUser, signOut } = useUsers();
  const { addSuccessNotification } = useNotifications();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    const params = {
      data: {
        type: "users",
        attributes: {
          status: "delete_in_process",
        },
      },
    };

    return updateUser(params).then(() => {
      addSuccessNotification("Your account has been flagged for deletion.");
      trackEventDeletionRequest();
      handleClose();
      signOut();
    });
  };

  const ContactSupportButton = () => {
    return (
      <a target="_blank" rel="noreferrer noopener" href={externalRoutes.requestHelpPath()}>
        <Button variant="outlined" color="primary" className={classes.button}>
          Contact Support
        </Button>
      </a>
    );
  };

  const PrivacyLink = () => {
    return (
      <NavLink target="_blank" to={appRoutes.privacyPath()} className={classes.link}>
        here
      </NavLink>
    );
  };

  const deleteBody = () => (
    <>
      <Typography component="p" className={classes.description}>
        Requesting deletion of your Personal Data will result in the deletion of your uploaded faces, personalized
        Ecards, and your account information. You will be unable to login to your account, and you will be unsubscribed
        from all our newsletters and transactional emails. This process is irreversible, and we will not be able to
        service your account once completed. This includes refunding any membership amounts.
      </Typography>

      <Typography component="p" className={classes.description}>
        Please note that we may retain your Personal Data for an additional period of time, as described in our Privacy
        Policy. Learn more about what Personal Data we collect and how we use and share your Personal Data{" "}
        <PrivacyLink />.
      </Typography>

      <ContactSupportButton />

      <Button variant="contained" color="primary" className={classes.deleteButton} onClick={handleDelete}>
        Delete my Personal Data
      </Button>

      <LinkStyledButton handleClick={handleClose}>I changed my mind</LinkStyledButton>
    </>
  );

  const paidBody = () => (
    <>
      <Typography component="p" className={classes.description}>
        You have an active membership. Before we can delete your Personal Data, you must end your membership by
        contacting us. Learn more about what Personal Data we collect and how we use and share your Personal Data{" "}
        <PrivacyLink />.
      </Typography>

      <ContactSupportButton />
    </>
  );

  const DeleteAccountDialog = () => {
    return (
      <Dialog
        onClose={handleClose}
        open={open}
        scroll="body"
        classes={{ root: classes.root }}
        PaperProps={{ classes: { root: classes.paper } }}
      >
        <SpinningCloseButton onClick={handleClose} />

        <DialogTitle disableTypography classes={{ root: classes.title }}>
          Delete My Personal Data
        </DialogTitle>

        {currentUser && (UserPresenter.isPaid(currentUser) ? paidBody() : deleteBody())}
      </Dialog>
    );
  };

  return (
    <div className={classes.section}>
      <LinkStyledButton handleClick={handleOpen}>Delete My Personal Data</LinkStyledButton>

      <DeleteAccountDialog />
    </div>
  );
};

export default DeleteAccount;
